/**
 * Protected Route component for authentication-gated routes.
 *
 * Wraps routes that require authentication, redirecting unauthenticated
 * users to the login page while showing a loading spinner during auth check.
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Props for ProtectedRoute component.
 */
interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Protected Route component.
 *
 * Renders children only when user is authenticated.
 * Shows loading spinner while checking authentication status.
 * Redirects to /login when user is not authenticated.
 *
 * @param props - Component props with children to protect
 */
export function ProtectedRoute({ children }: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();

  // Show spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner label="Checking authentication..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render protected content when authenticated
  return <>{children}</>;
}
